import React, { useContext, useEffect, useState } from 'react';

import {
  PopupMenu,
  TextButton,
  Table,
  Column,
  getPageSizeFromQueryString,
} from 'design-system-web';
import { useTranslation } from 'react-i18next';

import { gql, useLazyQuery, useMutation } from '@apollo/client';
import { listChargesQuery } from 'graphql-queries';
import { useQueryString } from 'hooks';
import {
  Charge,
  ExternalActorType,
  CHARGE_STATUS,
} from 'lane-shared/domains/billingPayments/types';
import { InvoiceStatusEnum } from 'domains-billing-types';
import {
  useWorkOrdersPaymentsManagedExternallyEnabled,
  useChargesFilteringV2Enabled,
} from 'react-hooks';
import { INVOICEID_NO_INVOICE } from 'application-types';
import { GroupType } from 'graphql-query-contracts';
import { routes } from 'client-config';
import { bpErrorsCodes } from 'activate-errors';
import {
  getMessagesFromError,
} from 'lane-shared/helpers';
import { getTimeZoneByGeoLocation } from 'async-timing';
import { dates } from 'lane-shared/helpers/constants';
import { convertToUUID, convertTo62, isUUID } from 'uuid-encoding';
import { getChargeStatus } from 'lane-shared/helpers/features';
import {
  dateFormatter,
  getThreeLetterTimeZoneAbbreviation,
} from 'lane-shared/helpers/formatters';
import { formatCurrencyWithSymbolOnly, currencyToCode } from 'domains-billing-currency';
import {
  graphQLToCurrency,
  currencyToPaymentCurrency
} from 'domains-billing-web-mappers';
import {
  ChargeQueryString,
  DEFAULT_FILTER_PARAMS,
  useGetChargeTableFilters,
} from './helpers/useGetChargeTableFilters';
import { buildChargeFilters } from './helpers/buildChargeFilters';
import { useUserDataContext } from 'lane-shared/hooks';
import useUserLocale from 'hooks/useUserLocale';

import { Chip, ChipStyle } from 'components/ads';

import ChannelAdminContext from '../ChannelAdminContext';

const voidChargeMutation = gql`
  mutation voidCharge($voidChargeRequest: VoidChargeRequest!) {
    accounts {
      voidCharge(voidChargeRequest: $voidChargeRequest) {
        chargeId
      }
    }
  }
`;

export function ChargesTable() {
  const { t } = useTranslation();
  const { channel } = useContext(ChannelAdminContext);
  const { user } = useUserDataContext();

  const isWorkOrdersPaymentsManagedExternallyEnabled =
    useWorkOrdersPaymentsManagedExternallyEnabled();
  const isChargesFilteringV2Enabled = useChargesFilteringV2Enabled();

  const [voidCharge, voidChargeResponse] = useMutation(voidChargeMutation);
  const timeZone = channel?.address?.geo
    ? getTimeZoneByGeoLocation({
      longitude: channel.address.geo[0],
      latitude: channel.address.geo[1],
    })
    : '';
  const locale = useUserLocale();

  function handleVoidChargeError(err: unknown) {
    const messages = getMessagesFromError(err);

    if (messages.length > 0) {
      if (
        messages.some(message =>
          message.includes(bpErrorsCodes.chargeAlreadyVoided.code)
        )
      ) {
        window.Toast.show(t(bpErrorsCodes.chargeAlreadyVoided.message));
      } else if (
        messages.some(message =>
          message.includes(bpErrorsCodes.chargeAlreadyPaid.code)
        )
      ) {
        window.Toast.show(t(bpErrorsCodes.chargeAlreadyPaid.message));
      } else {
        window.Toast.show(t(bpErrorsCodes.chargeVoidFailed.message));
      }
    }
  }

  const actions: any[] = [
    {
      label: t('abp.actions.voidCharge'),
      onClick: async (_id: number, charge: Charge) => {
        try {
          const domainCurrency = graphQLToCurrency(charge.currency);

          await window.Alert.confirm({
            title: t('abp.charge.voidChargeTitle'),
            message: t('abp.charge.voidChargeMessage', {
              currency: currencyToCode(domainCurrency),
              amount: charge.amount,
              name: charge.externalPayerId,
            }),
            confirmText: t('abp.actions.voidCharge'),
          });
          window.Alert.loading({
            title: 'Updating',
          });
          // User confirmed
          await voidCharge({
            variables: {
              voidChargeRequest: {
                chargeId: charge.id,
                externalActorId: user?._id || '',
                externalActorType:
                  ExternalActorType.EXTERNAL_ACTOR_TYPE_ACTIVATE_USER,
              },
            },
          });

          if (voidChargeResponse.error) {
            throw voidChargeResponse.error;
          }

          window.Toast.show(t('abp.charges.chargeVoidedSuccessfully'));
        } catch (err) {
          handleVoidChargeError(err);
          console.error(`Action cancelled: ${err}`);
        } finally {
          window.Alert.hide();
        }
      },
    },
  ];

  const chargeColumns: Column<Charge>[] = [
    {
      key: 'status',
      header: t('abp.chargeList.status'),
      renderCell: (cell: string, row) => {
        const statusMap = getChargeStatus(cell, row);
        const isVoided = statusMap[0] === CHARGE_STATUS.CHARGE_VOIDED;
        const isExternallyManaged =
          row.invoice?.status === InvoiceStatusEnum.EXTERNALLY_MANAGED;

        if (isExternallyManaged && !isVoided) {
          return null;
        }

        if (
          isWorkOrdersPaymentsManagedExternallyEnabled &&
          !isVoided &&
          row.invoiceId === INVOICEID_NO_INVOICE
        ) {
          return null;
        }

        const chipColor = statusMap[1] as ChipStyle;

        return <Chip value={statusMap[0]} type={chipColor} size="xs" />;
      },
    },
    { key: 'externalPayerId', header: t('abp.chargeList.customer') },
    {
      key: 'name',
      header: t('abp.chargeList.name'),
      renderCell: (cell: string, row: Charge) => {
        const isSR =
          row.externalEntityType === 'EXTERNAL_ENTITY_TYPE_WORKORDER';

        if (!isSR || !row.externalEntityId || !channel?.slug) {
          return <span>{cell}</span>;
        }

        const srId = isUUID(row.externalEntityId)
          ? convertTo62(row.externalEntityId)
          : row.externalEntityId;

        const href =
          routes.channelAdminWorkOrdersServiceRequestDetails
            .replace(':id', channel.slug)
            .replace(':serviceRequestId', srId) +
          `?tab=billing`;

        return (
          <a href={href} target="_blank" rel="noopener noreferrer">
            {cell}
          </a>
        );
      },
    },
    { key: 'description', header: t('abp.chargeList.description') },
    {
      key: 'currency',
      header: t('abp.chargeBillingReports.currency'),
      renderCell: (cell: number, row) => (
        <span>
          {currencyToPaymentCurrency(graphQLToCurrency(row.currency))}
        </span>
      ),
    },
    {
      key: 'amount',
      header: t('abp.chargeList.amount'),
      renderCell: (cell: number, row) => (
        <span>
          {formatCurrencyWithSymbolOnly(graphQLToCurrency(row.currency), row.amount, locale)}
        </span>
      ),
    },
    {
      key: 'createdAt',
      header: `${t(
        'abp.chargeList.createdDate'
      )} ${getThreeLetterTimeZoneAbbreviation(timeZone)}`,
      renderCell: (cell: string) => (
        <span>{dateFormatter(cell, dates.SIMPLE_DATE, timeZone)}</span>
      ),
    },
    {
      key: 'actions',
      header: t('abp.actions'),
      renderCell: (cell: number, row: Charge) => (
        <PopupMenu
          trigger={
            <TextButton
              iconName="angle-down"
              disabled={
                getChargeStatus(row.status, row)[0] ===
                CHARGE_STATUS.CHARGE_VOIDED ||
                getChargeStatus(row.status, row)[0] ===
                CHARGE_STATUS.CHARGE_PAID
              }
            >
              {t('abp.actions')}
            </TextButton>
          }
          items={actions.map(({ label, onClick }) => ({
            label,
            onSelect: () => {
              onClick(cell, row);
            },
          }))}
        />
      ),
    },
  ];

  const [channelId, setChannelId] = useState<string | null>(null);
  const chargeTableFilters = useGetChargeTableFilters(channelId);

  const [loadChargesList, { data, loading }] = useLazyQuery(listChargesQuery);

  const [filterParams, setFilterParams] = useQueryString<ChargeQueryString>(
    DEFAULT_FILTER_PARAMS
  );

  useEffect(() => {
    if (channel) {
      setChannelId(channel._id);

      loadChargesList({
        variables: {
          listChargesRequest: {
            groupId: convertToUUID(channel._id),
            groupType: GroupType.GroupTypeActivateChannel,
            pagination: {
              start:
                ((filterParams?.page || 0) as number) *
                getPageSizeFromQueryString(filterParams?.pageSize),
              perPage: getPageSizeFromQueryString(filterParams?.pageSize),
            },
            chargesFilters: isChargesFilteringV2Enabled
              ? buildChargeFilters(filterParams)
              : undefined,
          },
        },
      });
    }
  }, [
    channel?._id,
    loadChargesList,
    filterParams?.page,
    filterParams?.pageSize,
    filterParams?.status,
    filterParams?.createdAt,
    filterParams?.payer,
    isChargesFilteringV2Enabled,
  ]);

  const totalCharges = data?.accounts?.listCharges?.pageInfo?.total;

  useEffect(() => {
    if (totalCharges) {
      setFilterParams({
        total: totalCharges,
      });
    }
  }, [data?.accounts?.listCharges?.pageInfo?.total]);

  return (
    <Table
      key={String(isChargesFilteringV2Enabled)}
      columns={chargeColumns}
      data={data ? data.accounts.listCharges.charges : []}
      disableSorting
      isLoading={loading}
      pagination="server"
      totalRows={Number(filterParams.total)}
      queryStringsEnabled
      filters={isChargesFilteringV2Enabled ? chargeTableFilters : []}
      tableKey="chargesTable"
    />
  );
}
